import { FormActions } from "@solomon-apps/shared/src/components/ui/form-actions";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { ChevronLeftIcon } from "@solomon-labs/design/components/icons";
import React from "react";

import type { Program } from "../../shared/programMocks";
import type { FundingAccount } from "../fundingMocks";
import { useDraftProgram } from "../hooks/useDraftProgram";
import { ProgramConfiguration } from "../ProgramConfiguration";
import { ProgramHeader } from "../ProgramHeader";
import { ProgramKeyStats } from "../ProgramKeyStats";
import { ProgramPolicies } from "../ProgramPolicies";
import { ProgramProgress } from "../ProgramProgress";
import type { BuilderConfig } from "../Programs";
import { QualifiedUsers } from "../QualifiedUsers";

export function ProgramDetail({
    program,
    fundingAccounts,
    activationError,
    onActivate,
    onBack
}: {
    program: Program;
    fundingAccounts: FundingAccount[];
    activationError?: string | null;
    onActivate: (programId: string, config: BuilderConfig) => void;
    /**
     * Single-screen (mobile) master-detail: when set, a sticky back bar
     * returns to the program list. Omitted on desktop, where the list rail
     * sits beside the detail.
     */
    onBack?: () => void;
}): React.ReactElement {
    // The draft-builder logic layer — all local draft state, policy/funding
    // handlers, and the BuilderConfig assembly. Fields + setters match the
    // section DCs' prop interfaces, so they spread straight down.
    const draft = useDraftProgram(program);

    // Active programs are locked summaries (read-only config / budget +
    // key stats); only draft programs are editable in the builder.
    // "isActive" here means non-draft (active OR ended) → read-only detail
    // view. Only drafts (in_progress) get the editable builder.
    const isActive = program.status !== "in_progress";

    return (
        <div className="flex-1 overflow-auto">
            {/* Mobile-only back bar — returns to the program list in the
                single-screen master-detail flow. Sticky so it stays reachable
                while scrolling the long detail. */}
            {onBack ? (
                <button
                    type="button"
                    onClick={onBack}
                    className="sticky top-0 z-10 flex w-full items-center gap-1.5 border-b border-line-base bg-surface-ground px-4 py-3 text-foreground-muted transition-colors hover:text-foreground-base"
                >
                    <ChevronLeftIcon className="size-4" />
                    <Text variant="small">All programs</Text>
                </button>
            ) : null}

            {/* Title section — the mode-aware `ProgramHeader` DC. Draft:
                ProgramNameField + StatusBadge. Active: ProgramTitle (Heading)
                + StatusBadge + a Tags row + the NextDistribution meter. */}
            <ProgramHeader
                program={program}
                name={draft.name}
                onChangeName={draft.setName}
                regions={[program.region]}
                fundingAccounts={fundingAccounts}
            />

            {/* Key stats — active programs only. Unified onto the shared
                `KeyStatsRow` GC (via the `ProgramKeyStats` DC), matching the
                Portfolio / position-detail stat rows. Base APY folds in here
                as a 5th cell for continuous / open-budget programs. */}
            {isActive && (
                <>
                    <ProgramKeyStats program={program} />
                    <ProgramProgress program={program} />
                </>
            )}

            {/* Configuration — the DC owns the create/view fork (draft form
                vs read summary) on program.status. */}
            <ProgramConfiguration
                program={program}
                programType={draft.programType}
                setProgramType={draft.setProgramType}
                budgetMode={draft.budgetMode}
                setBudgetMode={draft.setBudgetMode}
                apyMode={draft.apyMode}
                setApyMode={draft.setApyMode}
                baseApy={draft.baseApy}
                setBaseApy={draft.setBaseApy}
                userPct={draft.userPct}
                setUserPct={draft.setUserPct}
                periodDays={draft.periodDays}
                setPeriodDays={draft.setPeriodDays}
                settlementCycleLength={draft.settlementCycleLength}
                setSettlementCycleLength={draft.setSettlementCycleLength}
                settlementCycleUnit={draft.settlementCycleUnit}
                setSettlementCycleUnit={draft.setSettlementCycleUnit}
                budget={draft.budget}
                setBudget={draft.setBudget}
            />

            {/* Policies — the DC owns the create/view fork (editor + Suggested
                + removable list vs edit-in-place list) and the editor UI
                state; policies/values live here (they feed BuilderConfig). */}
            <ProgramPolicies
                program={program}
                policies={draft.policies}
                policyValues={draft.policyValues}
                onAddPolicy={draft.addPolicy}
                onRemovePolicy={draft.removePolicy}
                onChangePolicyValue={draft.setPolicyValue}
            />

            {/* Qualified Users — active/ended programs only. The qualified
                holder/agent roster, its own bottom section (last in the read
                view), composed from Section + Table + ParticipantRow. */}
            {isActive && <QualifiedUsers program={program} />}

            {/* Draft programs end with the create CTA. */}
            {!isActive && (
                <section className="mt-8 mb-10 px-4 flex items-center gap-3">
                    <FormActions
                        size="lg"
                        primaryLabel="Add Program"
                        onPrimary={() =>
                            onActivate(program.id, draft.toBuilderConfig())
                        }
                        cancelLabel="Cancel"
                        onCancel={() => {}}
                    />
                    {activationError ? (
                        <Text variant="fine-print" className="text-error-base">
                            {activationError}
                        </Text>
                    ) : null}
                </section>
            )}
            {isActive && activationError ? (
                <section className="mt-4 mb-10 px-4">
                    <Text variant="fine-print" className="text-error-base">
                        {activationError}
                    </Text>
                </section>
            ) : null}
        </div>
    );
}
